import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { ArrowsLeftRight, Receipt as ReceiptIcon, ArrowDown, ArrowUp, CaretUpDown, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { CategoryCombo } from '@/pages/TransactionPage';

const fmt = (n) => `\u20B9${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * QuickFinanceDialog — fullscreen popup wrapper that lets the user record a
 * Transfer / Receipt / Income / Expense without leaving the Invoice page.
 *
 * Each mode posts directly to the existing backend endpoint and closes the
 * dialog on success. The invoice draft on the underlying page stays intact.
 *
 * Props:
 *   open                  – visibility flag
 *   onOpenChange(flag)    – close callback
 *   mode                  – 'transfer' | 'receipt' | 'income' | 'expense'
 *   onSaved()             – optional notify-parent so the invoice page can
 *                            refresh its account-balance pills after save.
 */
export default function QuickFinanceDialog({ open, onOpenChange, mode, onSaved, defaults }) {
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Shared form state — only the fields relevant to the active mode are read.
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    from_account: '', to_account: '', account: '',
    customer_id: '', category: '', amount: '', description: '', date: today, head_account: '',
  });

  useEffect(() => {
    if (!open) return;
    // Reset to a clean slate every time the dialog opens, then layer the
    // `defaults` (passed by callers like Customer Ledger > Collect Due)
    // so the form pre-fills with the customer + amount in one go.
    setForm({
      from_account: '', to_account: '', account: '',
      customer_id: '', category: '', description: '', date: today, head_account: '',
      ...(defaults || {}),
      // amount comes in as a number — keep it as a string for the input.
      amount: defaults?.amount != null ? String(defaults.amount) : '',
    });
    api.get('/finance/accounts').then(r => setAccounts(r.data || [])).catch(() => setAccounts([]));
    if (mode === 'receipt') {
      api.get('/customers').then(r => setCustomers(r.data || [])).catch(() => setCustomers([]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  // Same rule as the main pickers: hide credit cards that hold a
  // positive (over-paid) balance — only show ones with balance ≤ 0.
  // Creditor accounts always shown regardless of balance.
  const moneyAccounts = accounts.filter(a => {
    if (mode === 'expense') return ['cash', 'bank', 'wallet'].includes(a.type);
    if (['cash', 'bank', 'wallet', 'creditor'].includes(a.type)) return true;
    if (a.type === 'credit_card') return (a.balance || 0) <= 0;
    return false;
  });

  // Per-mode header chrome.
  const modeConfig = {
    transfer: { title: 'Fund Transfer', desc: 'Move money between accounts', accent: 'bg-[#F59E0B]', icon: ArrowsLeftRight, label: 'Transfer Funds' },
    receipt:  { title: 'Receipt',       desc: 'Record a receipt from a customer (parked as advance)', accent: 'bg-[#10B981]', icon: ReceiptIcon, label: 'Save Receipt' },
    income:   { title: 'Income',        desc: 'Record money received outside an invoice', accent: 'bg-[#10B981]', icon: ArrowDown, label: 'Save Income' },
    expense:  { title: 'Payment',       desc: 'Pay an expense, a creditor or capital drawings from Cash / Bank', accent: 'bg-[#EF4444]', icon: ArrowUp, label: 'Save Payment' },
  }[mode] || {};

  const handleSave = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
    setSubmitting(true);
    try {
      const ts = form.date + 'T' + new Date().toISOString().split('T')[1];
      if (mode === 'transfer') {
        if (!form.from_account || !form.to_account) { toast.error('Pick both accounts'); setSubmitting(false); return; }
        if (form.from_account === form.to_account) { toast.error('From and To must differ'); setSubmitting(false); return; }
        await api.post('/finance/transfer', {
          from_account: form.from_account, to_account: form.to_account,
          amount: amt, description: form.description, created_at: ts,
        });
        toast.success('Transfer recorded');
      } else if (mode === 'receipt') {
        if (!form.customer_id || !form.account) { toast.error('Customer + account required'); setSubmitting(false); return; }
        const res = await api.post('/finance/receipt', {
          customer_id: form.customer_id, amount: amt, payment_mode: form.account,
          date: form.date, description: form.description,
        });
        toast.success(`Receipt ${res.data?.voucher_number || ''} recorded`);
      } else if (mode === 'expense' && form.head_account) {
        if (!form.account) { toast.error('Account required'); setSubmitting(false); return; }
        await api.post('/finance/transfer', {
          from_account: form.account, to_account: form.head_account, amount: amt,
          description: form.description || `Payment to ${form.category}`, created_at: ts, payment_head: true,
        });
        toast.success(`Payment to ${form.category} recorded`);
      } else {
        // income / expense
        if (!form.account || !form.category) { toast.error('Account + category required'); setSubmitting(false); return; }
        await api.post('/finance', {
          type: mode, category: form.category, amount: amt,
          account: form.account, description: form.description, created_at: ts,
        });
        toast.success(`${mode === 'income' ? 'Income' : 'Payment'} recorded`);
      }
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const fromAcc = accounts.find(a => a.id === form.from_account);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-xl border-[#E4E4E7] max-w-2xl w-[92vw] sm:w-full max-h-[88vh] p-6 sm:p-7 flex flex-col gap-4 overflow-hidden"
        data-testid={`quick-${mode}-dialog`}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            {modeConfig.icon && <modeConfig.icon size={20} weight="duotone" className={`text-[#0A0A0A] p-1 rounded-md ${modeConfig.accent} text-white`} />}
            {modeConfig.title}
          </DialogTitle>
          <DialogDescription>{modeConfig.desc}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-lg mt-1" data-testid="qf-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Amount *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#A1A1AA]">&#8377;</span>
                <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="rounded-lg pl-7 font-mono" placeholder="0" data-testid="qf-amount" />
              </div>
              {mode === 'transfer' && fromAcc && form.amount && (
                <p className="text-xs mt-1 text-[#52525B]">
                  Available: <span className="font-mono">{fmt(fromAcc.balance || 0)}</span> &rarr; After: <span className={`font-mono ${(fromAcc.balance || 0) - parseFloat(form.amount || 0) < 0 ? 'text-[#EF4444]' : ''}`}>{fmt((fromAcc.balance || 0) - parseFloat(form.amount || 0))}</span>
                </p>
              )}
            </div>

            {mode === 'transfer' && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">From Account *</Label>
                  <Select value={form.from_account} onValueChange={v => setForm({ ...form, from_account: v })}>
                    <SelectTrigger className="rounded-lg mt-1" data-testid="qf-from-account"><SelectValue placeholder="Select source..." /></SelectTrigger>
                    <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name} &mdash; {fmt(a.balance)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">To Account *</Label>
                  <Select value={form.to_account} onValueChange={v => setForm({ ...form, to_account: v })}>
                    <SelectTrigger className="rounded-lg mt-1" data-testid="qf-to-account"><SelectValue placeholder="Select destination..." /></SelectTrigger>
                    <SelectContent>{accounts.filter(a => a.id !== form.from_account).map(a => <SelectItem key={a.id} value={a.id}>{a.name} &mdash; {fmt(a.balance)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}

            {mode === 'receipt' && (
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Customer *</Label>
                <CustomerInlinePicker
                  customers={customers}
                  value={form.customer_id}
                  onChange={(id) => setForm({ ...form, customer_id: id })}
                />
              </div>
            )}

            {(mode === 'receipt' || mode === 'income' || mode === 'expense') && (
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">{mode === 'receipt' ? 'Receiving Account *' : 'Account *'}</Label>
                <Select value={form.account} onValueChange={v => setForm({ ...form, account: v })}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="qf-account"><SelectValue placeholder="Select account..." /></SelectTrigger>
                  <SelectContent>{moneyAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            {mode === 'expense' && (
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Head *</Label>
                <CategoryCombo
                  value={form.category}
                  onChange={v => setForm({ ...form, category: v, head_account: '' })}
                  type="expense"
                  testId="qf-category"
                  headGroups={[
                    { label: 'Creditors', items: accounts.filter(a => a.type === 'creditor' && !a.system) },
                    { label: 'Capital', items: accounts.filter(a => a.type === 'capital') },
                  ]}
                  onSelectHead={a => setForm({ ...form, category: a.name, head_account: a.id })}
                />
              </div>
            )}

            {mode === 'income' && (
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Category *</Label>
                <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder={mode === 'income' ? 'e.g. Other Income' : 'e.g. Stationery'} className="rounded-lg mt-1" data-testid="qf-category" />
              </div>
            )}

            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional note" className="rounded-lg mt-1" data-testid="qf-description" />
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 sm:justify-end items-center gap-2 border-t border-[#E4E4E7] pt-4">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-lg" data-testid="qf-cancel"><X size={14} className="mr-1" /> Cancel</Button>
          <Button onClick={handleSave} disabled={submitting} className={`text-white rounded-lg ${modeConfig.accent} hover:opacity-90 hover:text-white`} data-testid="qf-save">
            {submitting ? 'Saving...' : modeConfig.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomerInlinePicker({ customers, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = (customers || []).filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.phone || '').includes(search)
  );
  const selected = customers.find(c => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between rounded-lg h-9 text-sm font-normal border-[#E4E4E7] hover:border-[#A1A1AA] hover:bg-white mt-1" data-testid="qf-customer">
          <span className={selected ? 'text-[#0A0A0A]' : 'text-[#A1A1AA]'}>
            {selected ? `${selected.name} ${selected.phone ? `· ${selected.phone}` : ''}` : 'Select customer...'}
          </span>
          <CaretUpDown size={12} className="ml-1 shrink-0 text-[#A1A1AA]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-lg" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search by name or phone..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-[#A1A1AA]">No match</CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 50).map(c => (
                <CommandItem key={c.id} value={c.id} onSelect={() => { onChange(c.id); setOpen(false); setSearch(''); }} className="cursor-pointer text-sm">
                  <span className="flex-1 truncate">
                    {c.name}
                    {c.phone && <span className="text-[10px] text-[#A1A1AA] ml-2">{c.phone}</span>}
                  </span>
                  {c.id === value && <Check size={14} className="text-[#3B82F6]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
